import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config';
import type { AuditAction, AuditResourceType } from '../../../../constants/enum';

interface RecordClientAuditEventParams {
  action: AuditAction;
  resource_type: AuditResourceType;
  resource_id?: string | null;
  /** ログイン失敗（未認証）時のみ指定。対象ユーザーの特定に使う */
  email?: string;
}

/**
 * クライアント発イベントの監査ログ記録（#2985）。
 * ログイン/ログアウト/ログイン失敗・パターン CRUD・権限変更・ユーザー削除など、
 * クライアント側で完結する操作を callable 経由でホット層に記録する。
 *
 * 監査ログの記録失敗で主処理（ログイン等）を止めないため、本関数は例外を
 * 送出しない（fire-and-forget）。await せずに呼び出してもよい。
 */
export const recordClientAuditEvent = async (
  params: RecordClientAuditEventParams,
): Promise<void> => {
  try {
    const callable = httpsCallable<RecordClientAuditEventParams, { success: boolean }>(
      functions,
      'recordClientAuditEvent',
    );
    await callable(params);
  } catch {
    // 監査ログの失敗は主処理に影響させない
  }
};

/**
 * 記録の完了を待つが、待ち時間に上限を設ける版（#3110）。
 * ログアウトのように「signOut 前に記録したいが、いつまでも待たせたくない」用途で使う。
 * タイムアウトしてもリクエスト自体は継続するため、通常はサーバー側に記録される。
 */
export const recordClientAuditEventWithTimeout = async (
  params: RecordClientAuditEventParams,
  timeoutMs: number,
): Promise<void> => {
  let timerId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>(resolve => {
    timerId = setTimeout(resolve, timeoutMs);
  });
  try {
    await Promise.race([recordClientAuditEvent(params), timeout]);
  } finally {
    if (timerId !== undefined) clearTimeout(timerId);
  }
};
